import { Users } from 'lucide-react';

import { RecordedLectures } from '@/components/lectures/RecordedLectures';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * Recorded classes, for the parent of the child on screen.
 *
 * Reads `GET /parent/children/:uuid/lectures`, the same server-side library the
 * child's own panel is drawn from, and draws it with the same
 * `RecordedLectures` — one catalogue, one player, one set of filters.
 *
 * Read-only. No bookmark or watch handler is passed, so the library shows
 * which lessons the child has watched but never writes to that record: a
 * parent pressing play is not the child watching, and a tick on the teacher's
 * roster must mean the child did.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentRecordedClassesPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.lectures(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  // Switching children refetches; until the new library lands, the old one
  // belongs to somebody else and must not be drawn under this child's name.
  const library = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, the lessons their teachers record appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-5')}>
      <RecordedLectures
        // Remounted per child, so a lesson open in the player or a filter set
        // for one child does not carry over to the next.
        key={child.uuid}
        list={library}
        isLoading={isLoading || (!library && !error)}
        error={error}
        onRetry={refetch}
        childName={child.fullName}
      />
    </div>
  );
}
